import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SquaresFour, PlusCircle, ChartBar, Users, Plug, ShoppingBag, ShieldCheck, SignOut, User, PlayCircle, Lock, Sun, Moon, Monitor } from '@phosphor-icons/react'
import * as Popover from '@radix-ui/react-popover'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { clearCrossUserLeakResidue } from '../lib/userScopedStorage'
import { hasBetterNotesAccess, hasIntegrationsAccess } from '../lib/featureFlags'
import { SUPPORTED_LOCALES, type SupportedLocale } from '../lib/i18n'
import NavLink from './NavLink'
import DynamicIsland from './DynamicIsland'
import BrandMonogramIcon from './betternotes/BrandMonogramIcon'

interface NavbarProps {
  className?: string
  /**
   * Layout variant.
   * - `default`: full app navbar (logo + nav links + DynamicIsland + profile menu + mobile bottom tab bar)
   * - `auth`: minimal chrome for /login, /signup, /forgot-password, /reset-password, /check-email, /email-confirmed
   *   — wordmark top-left + theme toggle top-right ONLY. No nav links (user isn't logged in yet),
   *   no DynamicIsland (no active analysis), no profile menu (no session), no mobile bottom tab bar.
   *   Keeps the same sticky chrome (backdrop-blur + border-bottom hairline) so wordmark position
   *   matches `/dashboard` cross-page (Mustapha decision 2026-04-25 hotfix 3b-i.3).
   */
  variant?: 'default' | 'auth'
}

export default function Navbar({ className = '', variant = 'default' }: NavbarProps) {
  const location = useLocation()
  const { t, i18n } = useTranslation('components')
  const { user, profile, refreshProfile } = useAuth()
  const { themeMode, setThemeMode, effectiveTheme } = useTheme()
  const [profileOpen, setProfileOpen] = useState(false)
  // Cycle dark → light → system → dark on each click. Single-button toggle
  // that shows the icon of the CURRENT effective theme — Mustapha 2026-04-20
  // asked for the toggle to come back to the top bar while keeping the
  // /profile → Apparence section as canonical source (mobile users get the
  // richer 3-pill control there). This button is desktop-only (`hidden lg:flex`).
  const cycleTheme = () => {
    const order: Array<typeof themeMode> = ['dark', 'light', 'system']
    const idx = order.indexOf(themeMode)
    setThemeMode(order[(idx + 1) % order.length])
  }
  const ThemeIcon = themeMode === 'system' ? Monitor : (effectiveTheme === 'dark' ? Moon : Sun)
  const themeLabel =
    themeMode === 'dark' ? t('navbar.theme_dark', { defaultValue: 'Thème sombre' })
    : themeMode === 'light' ? t('navbar.theme_light', { defaultValue: 'Thème clair' })
    : t('navbar.theme_system', { defaultValue: 'Thème système' })
  const currentLocale: SupportedLocale =
    i18n.language && (SUPPORTED_LOCALES as readonly string[]).includes(i18n.language)
      ? (i18n.language as SupportedLocale)
      : 'fr'

  const betterNotesEnabled = hasBetterNotesAccess(profile)
  const integrationsEnabled = hasIntegrationsAccess(profile)

  // Mobile bottom nav. `dataTour` values mirror the desktop NavLink
  // `data-tour` attrs so the FirstLoginTour spotlight finds the
  // correct target at the correct viewport — mobile users get the
  // same guided journey, just against the tab bar at the bottom.
  //
  // 2026-04-18 layout (Mustapha's call):
  //   · BetterNotes sits in the MIDDLE (slot 3 of 5) — the premium
  //     "star" slot mobile apps reserve for their key action
  //     (Instagram +, TikTok Create). On free plans the lock doubles
  //     as an upgrade driver.
  //   · Results is a first-class tab (analyses history = daily use).
  //   · Shop moves OUT of the tab bar and into a visible icon button
  //     next to the profile avatar in the top bar — rare payment
  //     action shouldn't take a bottom tab, but staying visible
  //     (vs. hidden in a popover) keeps the upgrade path one tap away.
  const mobileTabs = [
    { path: '/dashboard', label: t('navbar.dashboard'), icon: SquaresFour, dataTour: 'nav-dashboard' },
    { path: '/analyze', label: t('navbar.analyze'), icon: PlusCircle, dataTour: 'nav-analyze' },
    { path: '/betternotes', label: t('navbar.notes'), icon: BrandMonogramIcon, locked: !betterNotesEnabled, dataTour: 'nav-betternotes' },
    { path: '/results', label: t('navbar.results'), icon: ChartBar, dataTour: 'nav-results' },
    { path: '/crm', label: t('navbar.crm'), icon: Users, dataTour: 'nav-crm' },
  ]

  // Realtime credits
  useEffect(() => {
    if (!user) return
    const channel = supabase
      .channel(`navbar-credits-${user.id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` },
        () => { refreshProfile() }
      )
      .subscribe()
    return () => { channel.unsubscribe() }
  }, [user, refreshProfile])

  const handleLogout = () => {
    // Bypass supabase.auth.signOut() entirely — it can deadlock
    // indefinitely on Web Locks when the SDK's internal state is
    // corrupted by SPA client-side navigations. Instead, nuke the
    // auth session directly from localStorage (synchronous, instant).
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith('sb-'))
        .forEach(k => localStorage.removeItem(k))
      // Clear legacy unscoped keys + session checkout residue
      // (prevents cross-user state leaks on shared browsers)
      clearCrossUserLeakResidue()
    } catch { /* private browsing */ }
    // Hard reload — fresh Supabase client with no session = login page
    window.location.href = '/login'
  }

  // Auth variant — minimal chrome for unauthenticated pages. Wordmark
  // top-left + theme toggle top-right. Same sticky shell as the default
  // navbar so the wordmark position is identical cross-page (Auth = App
  // = Dashboard). Mobile bottom tab bar is intentionally NOT rendered
  // (user isn't logged in yet).
  if (variant === 'auth') {
    return (
      <nav className={`sticky top-0 z-40 backdrop-blur-2xl bg-[var(--bn-bg-sticky)] transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[1px] after:bg-[var(--bn-border)] ${className}`}>
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" reloadDocument className="flex items-center transition-opacity duration-150 hover:opacity-80">
              <div className="w-8 h-8 bg-[var(--bn-bg-surface-strong)] rounded-lg border border-[var(--bn-border)] flex items-center justify-center lg:hidden">
                <svg width="22" height="22" viewBox="0 0 120 120" fill="none">
                  <text x="14" y="42" fontFamily="Plus Jakarta Sans, sans-serif" fontWeight="700" fontSize="40" fill="#fafafa">B</text>
                  <line x1="44" y1="80" x2="76" y2="40" stroke="var(--bn-accent)" strokeWidth="5" strokeLinecap="round"/>
                  <text x="106" y="108" fontFamily="Plus Jakarta Sans, sans-serif" fontWeight="700" fontSize="40" fill="#fafafa" textAnchor="end">C</text>
                </svg>
              </div>
              <span className="hidden lg:block text-[17px] font-bold tracking-tight text-[var(--bn-text-primary)]">
                better<span className="text-[var(--bn-accent)]">/</span>close
              </span>
            </Link>
            <motion.button
              type="button"
              onClick={cycleTheme}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.92 }}
              aria-label={themeLabel}
              title={themeLabel}
              data-testid="navbar-theme-toggle"
              className="flex items-center justify-center w-8 h-8 rounded-full text-[var(--bn-text-muted)] hover:text-[var(--bn-text-primary)] hover:bg-[var(--bn-bg-surface-hover)] transition-colors duration-150"
            >
              <ThemeIcon className="w-[18px] h-[18px]" weight="regular" />
            </motion.button>
          </div>
        </div>
      </nav>
    )
  }

  return (
    <>
    <nav className={`sticky top-0 z-40 backdrop-blur-2xl bg-[var(--bn-bg-sticky)] transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[1px] after:bg-[var(--bn-border)] ${className}`}>
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid items-center h-14 sm:h-16" style={{ gridTemplateColumns: '1fr auto 1fr' }}>
          {/* ── LEFT: Logo + 3 nav links ── */}
          <div className="flex items-center gap-2 sm:gap-3 justify-self-start">
            <Link to="/dashboard" reloadDocument className="flex items-center transition-opacity duration-150 hover:opacity-80">
              <div className="w-8 h-8 bg-[var(--bn-bg-surface-strong)] rounded-lg border border-[var(--bn-border)] flex items-center justify-center lg:hidden">
                <svg width="22" height="22" viewBox="0 0 120 120" fill="none">
                  <text x="14" y="42" fontFamily="Plus Jakarta Sans, sans-serif" fontWeight="700" fontSize="40" fill="#fafafa">B</text>
                  <line x1="44" y1="80" x2="76" y2="40" stroke="var(--bn-accent)" strokeWidth="5" strokeLinecap="round"/>
                  <text x="106" y="108" fontFamily="Plus Jakarta Sans, sans-serif" fontWeight="700" fontSize="40" fill="#fafafa" textAnchor="end">C</text>
                </svg>
              </div>
              <span className="hidden lg:block text-[17px] font-bold tracking-tight text-[var(--bn-text-primary)]">
                better<span className="text-[var(--bn-accent)]">/</span>close
              </span>
            </Link>
            <div className="hidden lg:flex items-center gap-1 ml-1">
              <div className="w-px h-4 bg-[var(--bn-border)] mx-1.5" />
              <NavLink to="/dashboard" icon={SquaresFour} dataTour="nav-dashboard">{t('navbar.dashboard')}</NavLink>
              <NavLink to="/analyze" icon={PlusCircle} dataTour="nav-analyze">{t('navbar.analyze')}</NavLink>
              <NavLink to="/results" icon={ChartBar} dataTour="nav-results">{t('navbar.results')}</NavLink>
            </div>
          </div>

          {/* ── CENTER: Dynamic Island ── */}
          <div className="flex items-center justify-self-center">
            <DynamicIsland />
          </div>

          {/* ── RIGHT: 3 nav links + utils ──
              Narrower gap on mobile (gap-1.5 vs sm:gap-3) so the
              Shop button + Profile avatar always survive a fully
              EXPANDED DynamicIsland in the centre column (active
              analysis state). Grid is `1fr auto 1fr` — right col
              keeps its own territory, but on sub-400w phones the
              extra 8–12 px buffer matters. */}
          <div className="flex items-center gap-1.5 sm:gap-3 justify-self-end">
            <div className="hidden lg:flex items-center gap-1">
              <NavLink to="/crm" icon={Users} dataTour="nav-crm">{t('navbar.crm')}</NavLink>
              {/* BetterNotes always renders. Free-plan users see it
                  locked (Lock icon + muted tone). Clicking a locked
                  item still routes to `/betternotes`, where
                  BetterNotesLayout performs the hard paywall redirect
                  to `/shop?from=betternotes` — the upgrade moment. */}
              <NavLink
                to="/betternotes"
                icon={BrandMonogramIcon}
                dataTour="nav-betternotes"
                locked={!betterNotesEnabled}
              >
                {t('navbar.betternotes')}
              </NavLink>
              <NavLink to="/shop" icon={ShoppingBag} dataTour="nav-shop">{t('navbar.shop')}</NavLink>
              <div className="w-px h-4 bg-[var(--bn-border)] mx-1.5" />
            </div>
            {/* Theme toggle — desktop only. Single-button cycle
                (dark → light → system) that shows the current effective
                theme icon. Canonical source remains /profile → Apparence
                (mobile users tap their avatar and find the richer 3-pill
                control there). Restored to the top bar 2026-04-20 after
                Mustapha couldn't find it in-app. */}
            <motion.button
              type="button"
              onClick={cycleTheme}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.92 }}
              aria-label={themeLabel}
              title={themeLabel}
              data-testid="navbar-theme-toggle"
              className="hidden lg:flex items-center justify-center w-8 h-8 rounded-full text-[var(--bn-text-muted)] hover:text-[var(--bn-text-primary)] hover:bg-[var(--bn-bg-surface-hover)] transition-colors duration-150"
            >
              <ThemeIcon className="w-[18px] h-[18px]" weight="regular" />
            </motion.button>
            {/* Mobile-only Shop pill — sits RIGHT next to the profile
                avatar so the upgrade path stays one tap away without
                burning a bottom-tab slot. Brand purple (same palette as
                the Profile button for a cohesive Navbar row). No amber
                gradient (Mustapha 2026-04-21: "pas la couleur AI"). */}
            <Link
              to="/shop"
              reloadDocument
              data-testid="mobile-shop-pill"
              data-tour="nav-shop"
              aria-label={t('navbar.shop')}
              className="lg:hidden relative flex items-center justify-center w-8 h-8 rounded-full bg-[var(--bn-bg-surface-hover)] text-[var(--bn-text-soft)] hover:bg-[var(--bn-bg-surface-strong)] hover:text-[var(--bn-text-primary)] border border-[var(--bn-border)] hover:border-[var(--bn-border-strong)] ring-0 focus-visible:ring-2 focus-visible:ring-[var(--bn-accent)]/40 transition-colors duration-150"
            >
              <ShoppingBag className="w-4 h-4" weight="regular" />
            </Link>
            <Popover.Root open={profileOpen} onOpenChange={setProfileOpen}>
              <Popover.Trigger asChild>
                <motion.button
                  data-tour="profile-menu"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--bn-bg-surface-hover)] text-[var(--bn-text-soft)] hover:bg-[var(--bn-bg-surface-strong)] hover:text-[var(--bn-text-primary)] border border-[var(--bn-border)] hover:border-[var(--bn-border-strong)] ring-0 focus-visible:ring-2 focus-visible:ring-[var(--bn-accent)]/40 transition-colors duration-150"
                  aria-label={t('navbar.profile_menu_label')}
                >
                  <User className="w-4 h-4" weight="regular" />
                </motion.button>
              </Popover.Trigger>
              <AnimatePresence>
                {profileOpen && (
                  <Popover.Portal forceMount>
                    <Popover.Content
                      align="end"
                      sideOffset={8}
                      asChild
                    >
                      <motion.div
                        initial={{ opacity: 0, y: -4, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -4, scale: 0.95 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                        className="z-50 w-56 rounded-xl bg-[var(--bn-bg-surface-overlay)] border border-[var(--bn-border)] shadow-[var(--bn-shadow-lg)]"
                      >
                        <div className="px-3 py-3">
                          <p className="text-sm font-medium text-[var(--bn-text-primary)] truncate">
                            {profile?.first_name || t('navbar.default_user')}
                          </p>
                          <p className="text-xs text-[var(--bn-text-muted)] truncate">
                            {profile?.email}
                          </p>
                        </div>
                        <div className="border-t border-[var(--bn-border)]" />
                        <div className="px-3 py-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-[var(--bn-text-soft)]">{Math.round((profile?.credits ?? 0) * 10)} {t('navbar.credits_suffix')}</span>
                            <Link
                              to="/shop"
                              reloadDocument
                              onClick={() => setProfileOpen(false)}
                              className="text-xs text-[var(--bn-accent)] hover:text-[var(--bn-accent-deep)] transition-colors"
                            >
                              {t('navbar.top_up')}
                            </Link>
                          </div>
                        </div>
                        <div className="border-t border-[var(--bn-border)]" />
                        <div className="px-3 py-2 flex items-center justify-between">
                          <span className="text-xs text-[var(--bn-text-muted)] uppercase tracking-wider font-medium">{t('navbar.language')}</span>
                          <div className="flex gap-0.5 p-0.5 rounded-md bg-[var(--bn-segmented-track)]">
                            {SUPPORTED_LOCALES.map((lng) => (
                              <button
                                key={lng}
                                type="button"
                                onClick={() => { void i18n.changeLanguage(lng) }}
                                aria-pressed={currentLocale === lng}
                                className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${
                                  currentLocale === lng
                                    ? 'bg-[var(--bn-accent-bg-active)] text-[var(--bn-accent)]'
                                    : 'text-[var(--bn-text-muted)] hover:text-[var(--bn-text-primary)]'
                                }`}
                              >
                                {lng.toUpperCase()}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="border-t border-[var(--bn-border)]" />
                        <div className="p-1">
                          <Link
                            to="/profile"
                            reloadDocument
                            onClick={() => setProfileOpen(false)}
                            className="group flex items-center gap-2 px-3 py-2 text-sm text-[var(--bn-text-soft)] hover:text-[var(--bn-text-primary)] hover:bg-[var(--bn-bg-surface-hover)] rounded-lg transition-colors"
                          >
                            <User className="w-[18px] h-[18px] opacity-60 group-hover:opacity-100 transition-opacity" weight="regular" />
                            {t('navbar.my_profile')}
                          </Link>
                          <Link
                            to="/integrations"
                            reloadDocument
                            onClick={() => setProfileOpen(false)}
                            className="group flex items-center gap-2 px-3 py-2 text-sm text-[var(--bn-text-soft)] hover:text-[var(--bn-text-primary)] hover:bg-[var(--bn-bg-surface-hover)] rounded-lg transition-colors"
                            aria-label={integrationsEnabled ? t('navbar.integrations') : `${t('navbar.integrations')} — ${t('navbar.locked_label', { defaultValue: 'Plan payant requis' })}`}
                          >
                            <Plug className="w-[18px] h-[18px] opacity-60 group-hover:opacity-100 transition-opacity" weight="regular" />
                            <span className="flex-1">{t('navbar.integrations')}</span>
                            {!integrationsEnabled && (
                              <Lock
                                className="w-3.5 h-3.5 opacity-50 group-hover:opacity-80 transition-opacity"
                                weight="fill"
                                aria-hidden
                              />
                            )}
                          </Link>
                          <Link
                            to="/tutorials"
                            reloadDocument
                            onClick={() => setProfileOpen(false)}
                            className="group flex items-center gap-2 px-3 py-2 text-sm text-[var(--bn-text-soft)] hover:text-[var(--bn-text-primary)] hover:bg-[var(--bn-bg-surface-hover)] rounded-lg transition-colors"
                          >
                            <PlayCircle className="w-[18px] h-[18px] opacity-60 group-hover:opacity-100 transition-opacity" weight="regular" />
                            {t('navbar.tutorials')}
                          </Link>
                          {profile?.role === 'admin' && (
                            <Link
                              to="/admin"
                              reloadDocument
                              onClick={() => setProfileOpen(false)}
                              className="group flex items-center gap-2 px-3 py-2 text-sm text-[var(--bn-text-soft)] hover:text-[var(--bn-text-primary)] hover:bg-[var(--bn-bg-surface-hover)] rounded-lg transition-colors"
                            >
                              <ShieldCheck className="w-[18px] h-[18px] opacity-60 group-hover:opacity-100 transition-opacity" weight="regular" />
                              {t('navbar.admin')}
                            </Link>
                          )}
                        </div>
                        <div className="border-t border-[var(--bn-border)]" />
                        <div className="p-1">
                          <button
                            onClick={() => { setProfileOpen(false); handleLogout() }}
                            className="group flex items-center gap-2 w-full px-3 py-2 text-sm text-[var(--bn-state-error-text)] hover:bg-[var(--bn-state-error-bg)] rounded-lg transition-colors"
                          >
                            <SignOut className="w-[18px] h-[18px] opacity-60 group-hover:opacity-100 transition-opacity" weight="regular" />
                            {t('navbar.sign_out')}
                          </button>
                        </div>
                      </motion.div>
                    </Popover.Content>
                  </Popover.Portal>
                )}
              </AnimatePresence>
            </Popover.Root>
          </div>
        </div>
      </div>
    </nav>

    {/* ── MOBILE: Bottom Tab Bar ── */}
    <nav data-testid="mobile-tab-bar" className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[var(--bn-bg-sticky-strong)] backdrop-blur-xl border-t border-[var(--bn-border)] pb-[env(safe-area-inset-bottom)] ${location.pathname.startsWith('/result') && location.pathname !== '/results' ? 'hidden' : ''}`}>
      <div className="flex items-center justify-around px-2 pt-2 pb-1">
        {mobileTabs.map(tab => {
          const isActive = location.pathname === tab.path
          const isLocked = 'locked' in tab && tab.locked
          return (
            <Link
              key={tab.path}
              to={tab.path}
              reloadDocument
              data-tour={tab.dataTour}
              className="relative flex flex-col items-center gap-0.5 px-3 py-1.5 min-w-[56px]"
            >
              <motion.div whileTap={{ scale: 0.88 }} className="relative">
                {isActive && (
                  <motion.div
                    layoutId="mobile-tab-bg"
                    className="absolute inset-0 bg-[var(--bn-accent-bg-active)] rounded-xl -z-10"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <div className="relative">
                  <tab.icon
                    className={`w-[22px] h-[22px] mx-auto transition-colors ${
                      isActive
                        ? 'text-[var(--bn-accent)]'
                        : isLocked
                          ? 'text-[var(--bn-text-faint)]'
                          : 'text-[var(--bn-text-muted)]'
                    }`}
                    weight={isActive ? 'bold' : 'duotone'}
                  />
                  {/* Lock badge on locked mobile tab (free plans only).
                      Tiny violet dot with a Lock glyph, pinned top-right
                      of the icon so it's readable at 22 px. */}
                  {isLocked && (
                    <span
                      aria-hidden="true"
                      className="absolute -right-1.5 -top-1 flex h-[11px] w-[11px] items-center justify-center rounded-full border border-[var(--bn-bg-sticky)] bg-[var(--bn-accent)]"
                    >
                      <Lock className="h-[7px] w-[7px] text-white" weight="fill" />
                    </span>
                  )}
                </div>
                <span
                  className={`text-[10px] font-medium transition-colors ${
                    isActive
                      ? 'text-[var(--bn-accent)]'
                      : isLocked
                        ? 'text-[var(--bn-text-faint)]'
                        : 'text-[var(--bn-text-muted)]'
                  }`}
                >
                  {tab.label}
                </span>
                {isActive && (
                  <motion.div
                    layoutId="mobile-active-dot"
                    className="w-1 h-1 rounded-full bg-[var(--bn-accent)] mt-0.5 mx-auto"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
              </motion.div>
            </Link>
          )
        })}
      </div>
    </nav>
    </>
  )
}
